import * as React from "react";
import { ReactNode } from "react";
import { _NavItem } from "./_GlobalNav";
import { AppShell, Footer, Header } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import _BottomHeader from "./_BottomHeader";
import _TopHeader from "./_TopHeader";
import _Footer from "./_Footer";
import _MobileHeader from "./_MobileHeader";

interface VSETHExternalAppProps {
	/**
	title of the app
  	*/
	title: string;
	/**
	Nav structure of this app
  	*/
	appNav: _NavItem[];
	/**
	Nav structure of the organization
  	*/
	organizationNav?: _NavItem[];
	/**
	currently selected language key
  	*/
	selectedLanguage?: "en" | "de" | string;
	/**
	list of languages available for the nav
  	*/
	languages?: { key: string; label: string }[];
	/**
	function that gets called when the user selects the language in the nav
  	*/
	onLanguageSelect?: (lang: string) => void;
	/**
	function that wraps the nav elements in a custom link tag
  	*/
	makeWrapper?: (
		url: string | undefined,
		child: ReactNode
	) => ReactNode | JSX.Element;
	/**
	path to the current url
  	*/
	activeHref?: string;
	/**
	link to the logo
  	*/
	logo?: string;
	/**
	Show Login Button in header
  	*/
	showLoginButton?: boolean;
	/**
	link to the dislaimer
  	*/
	disclaimer?: string;
	/**
	link to the privacy policy
  	*/
	privacyPolicy?: string;
	/**
	link to the impressum
  	*/
	impressum?: string;
	/**
	List of social media icon/links to display in the footer
  	*/
	socialMedia?: {
		type: "facebook" | "twitter" | "instagram" | "email";
		link: string;
	}[];
	/**
	Children rendered in the main area
  	*/
	children?: React.ReactNode;
}

const VSETHExternalApp: React.FC<VSETHExternalAppProps> = ({
	selectedLanguage,
	languages,
	onLanguageSelect,
	appNav,
	organizationNav,
	title,
	makeWrapper,
	showLoginButton = false,
	children,
	disclaimer,
	privacyPolicy,
	impressum,
	activeHref,
	logo,
	socialMedia,
}) => {
	const desktop = useMediaQuery("(min-width: 900px)");
	const [_selectedLanguage, _onLanguageSelect] = React.useState(
		selectedLanguage ? selectedLanguage : "en"
	);

	React.useEffect(
		() => _onLanguageSelect(selectedLanguage ? selectedLanguage : "en"),
		[selectedLanguage]
	);

	return (
		<AppShell
			fixed={false}
			padding="md"
			header={
				<Header>
					<div style={{ height: "100%" }}>
						{desktop ? (
							<>
								<_TopHeader
									selectedLanguage={_selectedLanguage}
									languages={languages}
									onLanguageSelect={
										onLanguageSelect || ((lang) => _onLanguageSelect(lang))
									}
									organizationNav={organizationNav}
									logo={logo}
								/>
								<_BottomHeader
									lang={_selectedLanguage}
									title={title}
									appNav={appNav}
									makeWrapper={makeWrapper}
									activeHref={activeHref}
									showLoginButton={showLoginButton}
								/>
							</>
						) : (
							<_MobileHeader
								selectedLanguage={_selectedLanguage}
								languages={languages}
								onLanguageSelect={
									onLanguageSelect || ((lang) => _onLanguageSelect(lang))
								}
								title={title}
								appNav={appNav}
								makeWrapper={makeWrapper}
								showLoginButton={showLoginButton}
							/>
						)}
					</div>
				</Header>
			}
			footer={
				<Footer>
					<_Footer
						disclaimer={disclaimer}
						privacyPolicy={privacyPolicy}
						impressum={impressum}
						socialMedia={socialMedia}
					/>
				</Footer>
			}
		>
			{children}
		</AppShell>
	);
};

export default VSETHExternalApp;
